// src/utils/documentSetColors.js

/**
 * Predefined color palette for document sets
 * Properties:
 * - bg: Default background color
 * - text: Text color (high contrast)
 * - hover: Background color on hover
 * - select: Background color when selected
 */
export const DOCUMENT_SET_COLORS = [
  { name: 'Red', border: '#EFA2AF', hover: '#EE8D9F', text: '#52000F', bg: '#FFC2CD', select: '#E8647C' },
  { name: 'Orange', border: '#FDCD99', hover: '#FFC383', text: '#522A00', bg: '#FFE1C2', select: '#FFA84D' },
  { name: 'Yellow', border: '#FDE186', hover: '#FFDC6C', text: '#523E00', bg: '#FFEFBD', select: '#F8CE44' },
  { name: 'Green', border: '#8FE8AA', hover: '#76E599', text: '#09491D', bg: '#BFF3CF', select: '#48CB72' },
  { name: 'Blue', border: '#B5C1FA', hover: '#A5B4FC', text: '#020E4A', bg: '#D8DEFE', select: '#8D9CE2' },
  { name: 'Purple', border: '#D3B2FC', hover: '#CBA1FF', text: '#2A0656', bg: '#E6D1FF', select: '#A070DB' },
  { name: 'Gray', border: '#CBD0D5', hover: '#C0C7CE', text: '#0C141D', bg: '#E1E5EA', select: '#8F99A3' },
];

/**
 * Get a random color from the palette, avoiding colors already used in the case
 * @param {Array} existingDocumentSets - Array of existing document sets with color properties
 * @returns {object} - A color object from DOCUMENT_SET_COLORS
 */
export const getRandomColor = (existingDocumentSets = []) => {
  // Get colors already in use
  const usedColorNames = existingDocumentSets.map(set => set.color?.name).filter(Boolean);

  // Find available colors (not yet used)
  const availableColors = DOCUMENT_SET_COLORS.filter(
    color => !usedColorNames.includes(color.name)
  );

  // If we have available colors, pick one randomly
  if (availableColors.length > 0) {
    const randomIndex = Math.floor(Math.random() * availableColors.length);
    return availableColors[randomIndex];
  }

  // If all colors are used, we need to allow reuse
  // Strategy: avoid the most recently used color(s)
  // Get the last N colors used (where N = min(3, total sets))
  const recentCount = Math.min(3, existingDocumentSets.length);
  const recentColorNames = existingDocumentSets
    .slice(-recentCount)
    .map(set => set.color?.name)
    .filter(Boolean);

  // Find colors not in the recent set
  const nonRecentColors = DOCUMENT_SET_COLORS.filter(
    color => !recentColorNames.includes(color.name)
  );

  // If we have non-recent colors, pick one
  if (nonRecentColors.length > 0) {
    const randomIndex = Math.floor(Math.random() * nonRecentColors.length);
    return nonRecentColors[randomIndex];
  }

  // Fallback: just pick any random color
  const randomIndex = Math.floor(Math.random() * DOCUMENT_SET_COLORS.length);
  return DOCUMENT_SET_COLORS[randomIndex];
};

/**
 * Get color object by name
 */
export const getColorByName = (name) => {
  return DOCUMENT_SET_COLORS.find(c => c.name === name) || DOCUMENT_SET_COLORS[0];
};